import {Artifact, PasteAttachment} from "../../../types";

/**
 * A paste of more than this many lines becomes an attachment chip instead of
 * inline input text.
 */
export const PASTE_LINE_THRESHOLD = 3;

/**
 * Upper bound on the characters an attachment carries. Longer pastes are cut
 * here and the chip says so. The backend caps defensively at a higher number.
 */
export const PASTE_MAX_CHARS = 50000;

/** How many pastes one message may carry before the input turns them away. */
export const MAX_PASTE_ATTACHMENTS = 5;

/** Artifact type the backend stores an attachment under. */
export const ATTACHMENT_ARTIFACT_TYPE = "user_attachment";

/** The only attachment kind so far: text pasted into the composer. */
export const PASTE_ATTACHMENT_KIND = "paste";

/** A paste held in the input, not yet sent. */
export interface PendingPasteAttachment {
    id: string;
    content: string;
    lineCount: number;
    truncated: boolean;
}

/**
 * What a composer calls to submit. A `false` return means a guard rejected the
 * submit, and the input keeps both the text and the attachments.
 */
export type SubmitWithAttachments = (
    message: string,
    attachments: PendingPasteAttachment[]
) => boolean | void;

/** What a chip needs to render, pending or already sent. */
export interface PasteChipData {
    id: string;
    lineCount: number;
    truncated: boolean;
    /** Thread-scoped paste number, assigned by the backend on storage. */
    number?: number;
    /** The pasted text itself, so an in-thread chip can preview it. */
    content?: string;
}

export const countLines = (text: string) => text.split("\n").length;

/** Clipboard text arrives with any of the three line endings. */
export const normalizePastedText = (text: string) =>
    text.replace(/\r\n?/g, "\n");

export const isAttachablePaste = (text: string) =>
    countLines(text) > PASTE_LINE_THRESHOLD;

/** Cut at the limit without splitting a surrogate pair in two. */
function sliceAtCharLimit(text: string) {
    const cut = text.slice(0, PASTE_MAX_CHARS);
    const lastUnit = cut.charCodeAt(cut.length - 1);
    const endsInHighSurrogate = lastUnit >= 0xd800 && lastUnit <= 0xdbff;
    return endsInHighSurrogate ? cut.slice(0, -1) : cut;
}

export function createPendingPasteAttachment(
    text: string
): PendingPasteAttachment {
    const truncated = text.length > PASTE_MAX_CHARS;
    const content = truncated ? sliceAtCharLimit(text) : text;
    return {
        id: crypto.randomUUID(),
        content,
        lineCount: countLines(content),
        truncated,
    };
}

export const toRequestAttachments = (
    attachments: PendingPasteAttachment[]
): PasteAttachment[] =>
    attachments.map(({content, truncated}) => ({
        type: PASTE_ATTACHMENT_KIND,
        content,
        ...(truncated && {truncated}),
    }));

/**
 * Local stand-ins for the artifacts the backend creates, so the just-sent
 * message shows its chips — numbers included — before the thread is reloaded.
 * `firstNumber` comes from `nextPasteNumber` over the thread as it stands.
 */
export const toPasteArtifacts = (
    attachments: PendingPasteAttachment[],
    firstNumber: number
): Artifact[] =>
    attachments.map(({id, content, lineCount, truncated}, index) => ({
        id,
        type: ATTACHMENT_ARTIFACT_TYPE,
        schemaVersion: "1.0",
        source: "user" as const,
        payload: {
            attachmentType: PASTE_ATTACHMENT_KIND,
            number: firstNumber + index,
            lines: lineCount,
            chars: content.length,
            truncated,
            content,
        },
        createdAt: new Date().toISOString(),
    }));

/** Line count from the payload, recomputed from the content if it is absent. */
export const readPasteLineCount = (
    payload: Record<string, unknown> | undefined
) => {
    const {lines, content} = payload ?? {};
    return typeof lines === "number"
        ? lines
        : countLines(typeof content === "string" ? content : "");
};

export const readPasteChip = (artifact: Artifact): PasteChipData => {
    const {truncated, number, content} = artifact.payload;
    return {
        id: artifact.id,
        lineCount: readPasteLineCount(artifact.payload),
        truncated: truncated === true,
        ...(typeof number === "number" && {number}),
        ...(typeof content === "string" && {content}),
    };
};

export const isPasteArtifact = (artifact: Artifact) =>
    artifact.type === ATTACHMENT_ARTIFACT_TYPE &&
    artifact.payload.attachmentType === PASTE_ATTACHMENT_KIND;

/**
 * The number the next paste on this thread gets: one past the highest already
 * stored. This mirrors the backend, which counts from the artifacts that
 * survive on the thread, so an edited turn reuses the number it gave up.
 */
export const nextPasteNumber = (artifacts?: Artifact[]) =>
    (artifacts ?? [])
        .filter(isPasteArtifact)
        .reduce(
            (highest, {payload}) =>
                typeof payload.number === "number" && payload.number > highest
                    ? payload.number
                    : highest,
            0
        ) + 1;

export const readPasteChips = (artifacts?: Artifact[]): PasteChipData[] =>
    (artifacts ?? []).filter(isPasteArtifact).map(readPasteChip);

/** Paste content stored on a message, for re-sending an edited turn. */
export const readPasteAttachments = (
    artifacts?: Artifact[]
): PasteAttachment[] =>
    (artifacts ?? [])
        .filter(isPasteArtifact)
        .filter((artifact) => typeof artifact.payload.content === "string")
        .map(({payload}) => ({
            type: PASTE_ATTACHMENT_KIND,
            content: payload.content as string,
            ...(payload.truncated === true && {truncated: true}),
        }));
